import type {
  GetChatMessagesPagedRequest,
  PagedModelClientChatMessage
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useCommunicationChatEventsApi } from '@/features/api/hooks/communication-chat-events/useCommunicationChatEventsApi';
import { useApiMutation } from '@/features/api/hooks/useApiMutation';

export type CreateChatMessageParams = {
  created: number;
  message?: string;
  temporaryMediaItemIds?: string[];
};

export const useSendChatMessage = (chatId: string) => {
  const queryClient = useQueryClient();
  const api = useCommunicationChatEventsApi();

  return useApiMutation({
    mutationKey: [API_CACHE_KEYS.PENDING_CHAT_MESSAGES, chatId],
    mutationFn: async ({
      created,
      message,
      temporaryMediaItemIds
    }: CreateChatMessageParams) =>
      api.sendMessage({
        clientChatMessageSendRequest: {
          chatId,
          message,
          temporaryMediaItemIds,
          sentTime: created
        }
      }),
    onSuccess: (confirmation) => {
      queryClient.setQueryData<
        InfiniteData<PagedModelClientChatMessage, GetChatMessagesPagedRequest>
      >([API_CACHE_KEYS.CHAT_MESSAGES, chatId], (oldData) => {
        if (!oldData) {
          return oldData;
        }
        const pages = [...oldData.pages];
        const firstPage: PagedModelClientChatMessage = pages[0] ?? {
          content: []
        };
        const content = [
          confirmation.chatMessage,
          ...(firstPage.content ?? [])
        ];
        pages[0] = { ...firstPage, content };
        return { ...oldData, pages };
      });
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.CHAT_MESSAGES, chatId]
      });
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.CHAT, chatId]
      });
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.OWN_CHATS]
      });
    }
  });
};
